import { useState, Fragment, memo, useEffect, useCallback } from 'react';
import { Button, IconButton, TextFieldProps } from '@mui/material';
import { AddEditCustomViewForm, SearchInput, SelectInput } from '../../common';
import { FilterListIcon } from '../../../assets';
import { Navigation, authGet, listTypes } from '../../../lib';
import { useGetQueryParams, useUpdatePageQueryParams } from '../../../hooks';
import {
  useDispatch,
  useSelector,
  getBasicListCustomViews,
  updateListParams,
  uiSelectors,
} from '../../../state';
import { FiltersSelect } from './FiltersSelect';
import { FiltersSelectSingle } from './FiltersSelectSingle';
import { FiltersSearch } from './FiltersSearch';
import { FiltersAutocomplete } from './FiltersAutocomplete';
import { FiltersAutocompleteSingle } from './FiltersAutocompleteSingle';
import { FiltersDate } from './FiltersDate';
import { FiltersDateRange } from './FiltersDateRange';
import { FiltersDateTimeRange } from './FiltersDateTimeRange';
import { FiltersSwitch } from './FiltersSwitch';
import { useStyles } from './filters.styles';
import { ColumnVisibilityMenu } from '../ColumnVisibilityMenu';
import {
  FiltersProps,
  OrderByType,
  SearchInputProps,
  SelectOption,
} from '../../../types';
import {
  getColumnDefinitions,
  getColumnVisibilityMap,
} from '../columnVisibility';

interface Props {
  defaultSearchProps?: SearchInputProps & TextFieldProps;
  filtersProps?: FiltersProps;
  hasCustomViews?: boolean;
  hasDefaultSearchField?: boolean;
  order?: OrderByType;
  orderBy?: string;
  queryParams: object;
  resource: string;
  searchStyle?: React.CSSProperties;
  fields?: React.ReactNode;
  columnVisibility?: Record<string, boolean>;
}

function isObjectRecord(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function getCustomViewState(
  params: unknown,
  defaultColumnVisibility?: Record<string, boolean>,
) {
  if (!isObjectRecord(params)) {
    return {
      queryParams: {},
      columnVisibility: defaultColumnVisibility,
    };
  }

  if (
    Object.prototype.hasOwnProperty.call(params, 'queryParams') ||
    Object.prototype.hasOwnProperty.call(params, 'columnVisibility')
  ) {
    return {
      queryParams: isObjectRecord(params.queryParams) ? params.queryParams : {},
      columnVisibility: isObjectRecord(params.columnVisibility)
        ? params.columnVisibility
        : defaultColumnVisibility,
    };
  }

  return {
    queryParams: params,
    columnVisibility: defaultColumnVisibility,
  };
}

export const ListFiltersActions = memo(
  ({
    defaultSearchProps,
    filtersProps,
    hasCustomViews,
    hasDefaultSearchField,
    order,
    orderBy,
    queryParams,
    resource,
    searchStyle,
    fields,
    columnVisibility,
  }: Props) => {
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const updatePageQueryParams = useUpdatePageQueryParams();
    const [showFilters, setShowFilters] = useState(true);
    const [customViews, setCustomViews] = useState<Array<SelectOption>>([]);
    const [selectedCustomView, setSelectedCustomView] = useState(null);
    const [showCustomViewForm, setShowCustomViewForm] = useState(false);
    const [isNewCustomView, setIsNewCustomView] = useState(false);

    const { 'custom-view-id': customViewId } = useGetQueryParams();
    const viewVersion = useSelector(uiSelectors.viewVersion);
    const defaultColumnVisibility = getColumnVisibilityMap(
      getColumnDefinitions(fields),
    );

    useEffect(() => {
      (async function () {
        if (listTypes[resource]) {
          const { data } = await dispatch(
            getBasicListCustomViews(listTypes[resource]),
          );
          if (data) {
            setCustomViews(data);
          }
        }
      })();
    }, [dispatch, resource, viewVersion]);

    const getCustomViewByID = useCallback(
      async (id) => {
        const { data } = await authGet([`/system/custom-views/${id}`]);
        if (data) {
          const { order, orderBy, params } = data;
          const {
            queryParams: rawCustomViewQueryParams,
            columnVisibility: customViewColumnVisibility,
          } = getCustomViewState(params, defaultColumnVisibility);

          const {
            rowsPerPage: rawRowsPerPage,
            page: _pg,
            ...customViewQueryParams
          } = rawCustomViewQueryParams as Record<string, any>;
          const customViewRowsPerPage = rawRowsPerPage
            ? +rawRowsPerPage
            : undefined;

          setSelectedCustomView(data);

          // Reset previous query parameters to null
          const prevQueryParams = { ...queryParams };
          Object.keys(queryParams).forEach((key) => {
            prevQueryParams[key] = null; // Set each query parameter to null
          });

          const listParamsUpdate: Record<string, any> = {};
          if (customViewColumnVisibility) {
            listParamsUpdate.columnVisibility = customViewColumnVisibility;
          }
          if (customViewRowsPerPage) {
            listParamsUpdate.rowsPerPage = customViewRowsPerPage;
            listParamsUpdate.page = 0;
          }
          if (Object.keys(listParamsUpdate).length) {
            dispatch(
              updateListParams({
                list: resource,
                params: listParamsUpdate,
              }),
            );
          }

          // Update the URL
          updatePageQueryParams(
            {
              ...prevQueryParams,
              ...customViewQueryParams,
              'custom-view-id': id,
              order,
              orderBy,
              ...(customViewRowsPerPage
                ? { rowsPerPage: customViewRowsPerPage, page: 1 }
                : {}),
            },
            true,
          );
        }
      },
      [
        defaultColumnVisibility,
        dispatch,
        queryParams,
        resource,
        updatePageQueryParams,
      ],
    );

    useEffect(() => {
      (async function () {
        if (customViewId) {
          await getCustomViewByID(customViewId);
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewVersion]);

    const clearSelectedView = useCallback(() => {
      setSelectedCustomView(null);
      Navigation.redirect(`${window.location.pathname}`);
      setIsNewCustomView(true);
    }, []);

    const handleSelectCustomView = useCallback(
      async (e) => {
        if (e.target.value) {
          await getCustomViewByID(e.target.value);
          setIsNewCustomView(false);
        } else {
          clearSelectedView();
        }
      },
      [clearSelectedView, getCustomViewByID],
    );

    return (
      <div className={classes.root} style={searchStyle}>
        <div className={classes.firstRow}>
          <div style={{ display: 'flex' }}>
            {hasDefaultSearchField ? (
              <SearchInput {...defaultSearchProps} />
            ) : (
              <span />
            )}
            {hasCustomViews && (
              <div
                className={classes.customView}
                style={
                  hasDefaultSearchField
                    ? {
                        marginLeft: 24,
                      }
                    : {}
                }
              >
                <SelectInput
                  name='customView'
                  label='My Custom Views'
                  onChange={handleSelectCustomView}
                  options={customViews}
                  value={customViewId || ''}
                  clearable
                  style={{ width: 200 }}
                />
                {selectedCustomView && (
                  <Button
                    color='primary'
                    size='small'
                    style={{ textTransform: 'capitalize', marginLeft: 16 }}
                    onClick={() => setShowCustomViewForm(true)}
                  >
                    Update
                  </Button>
                )}
                <Button
                  color='primary'
                  size='small'
                  style={{ textTransform: 'capitalize', marginLeft: 16 }}
                  onClick={() => {
                    setIsNewCustomView(true);
                    setShowCustomViewForm(true);
                  }}
                >
                  {`Save as ${selectedCustomView ? 'new' : ''}`}
                </Button>
                {showCustomViewForm && (
                  <AddEditCustomViewForm
                    // {...(!isNewCustomView && { editData: selectedCustomView })}
                    editData={!isNewCustomView ? selectedCustomView : null}
                    clearSelectedView={clearSelectedView}
                    queryParams={queryParams}
                    columnVisibility={columnVisibility}
                    handleClose={() => setShowCustomViewForm(false)}
                    // handleSelectCustomView={handleSelectCustomView}
                    listType={listTypes[resource]}
                    open={showCustomViewForm}
                    order={order}
                    orderBy={orderBy}
                    setIsNewCustomView={setIsNewCustomView}
                  />
                )}
              </div>
            )}
          </div>
          <div className={classes.actionsRight}>
            {filtersProps?.actions}
            {!filtersProps?.hideColumnVisibility && (
              <ColumnVisibilityMenu
                resource={resource}
                fields={fields}
                columnVisibility={columnVisibility}
              />
            )}
            {!!filtersProps && !filtersProps.hideFilterToggle && (
              <IconButton
                onClick={() => setShowFilters(!showFilters)}
                color='primary'
                className={classes.filterBtn}
                size='large'
              >
                <FilterListIcon />
              </IconButton>
            )}
          </div>
        </div>
        {!!filtersProps && Array.isArray(filtersProps.filters) && (
          <Fragment>
            <div className={cx({ [classes.filterRow]: showFilters })}>
              {showFilters && filtersProps.leadingFilters}
              {filtersProps.filters.map((f, i) => {
                if (f.hide) return null;
                return (
                  <Fragment key={i}>
                    {(!f.filterType || f.filterType === 'select') &&
                      showFilters && (
                        <FiltersSelect
                          label={f.label}
                          filters={f.filter}
                          defaultValue={f.defaultValue}
                          filterSortIds={f.filterSortIds}
                          queryParam={f.queryParam}
                          clearable={f.clearable}
                          className={cx(classes.filterSelect, f.className)}
                          // style={{ marginTop: 15, ...(f.style || {}) }}
                        />
                      )}
                    {f.filterType === 'selectSingle' && showFilters && (
                      <FiltersSelectSingle
                        label={f.label}
                        defaultValue={f.defaultValue}
                        filters={f.filter}
                        filterSortIds={f.filterSortIds}
                        queryParam={f.queryParam}
                        clearable={f.clearable}
                        className={cx(classes.filterSelect, f.className)}
                        // style={{ marginTop: 15, ...(f.style || {}) }}
                      />
                    )}
                    {f.filterType === 'autocomplete' && showFilters && (
                      <FiltersAutocomplete
                        label={f.label}
                        filters={f.filter}
                        filterSortIds={f.filterSortIds}
                        queryParam={f.queryParam}
                        clearable={f.clearable}
                        className={cx(classes.autocomplete, f.className)}
                        // style={{ marginTop: 15, ...(f.style || {}) }}
                      />
                    )}
                    {f.filterType === 'autocompleteSingle' && showFilters && (
                      <FiltersAutocompleteSingle
                        label={f.label}
                        filters={f.filter}
                        filterSortIds={f.filterSortIds}
                        queryParam={f.queryParam}
                        clearable={f.clearable}
                        className={cx(classes.autocomplete, f.className)}
                        // style={{ marginTop: 15, ...(f.style || {}) }}
                      />
                    )}
                    {f.filterType === 'search' && showFilters && (
                      <FiltersSearch
                        label={f.label}
                        queryParam={f.queryParam}
                        clearable={f.clearable}
                        className={cx(classes.filterSelect, f.className)}
                        style={{ marginTop: 15, ...(f.style || {}) }}
                      />
                    )}
                    {f.filterType === 'date' && showFilters && (
                      <FiltersDate
                        label={f.label}
                        queryParam={f.queryParam}
                        defaultValue={f.defaultValue}
                        clearable={f.clearable}
                        className={cx(classes.filterSelect, f.className)}
                        datePickerProps={f.datePickerProps}
                        // style={{ marginTop: 15, ...(f.style || {}) }}
                      />
                    )}
                    {f.filterType === 'dateRange' && showFilters && (
                      <FiltersDateRange
                        label={f.label}
                        queryParams={f.queryParams}
                        defaultValues={f.defaultValues}
                        clearable={f.clearable}
                        className={cx(classes.filterSelect, f.className)}
                        // style={{ marginTop: 15, ...(f.style || {}) }}
                      />
                    )}
                    {f.filterType === 'dateTimeRange' && showFilters && (
                      <FiltersDateTimeRange
                        label={f.label}
                        queryParams={f.queryParams}
                        defaultValues={f.defaultValues}
                        clearable={f.clearable}
                        className={cx(classes.filterSelect, f.className)}
                      />
                    )}
                    {f.filterType === 'switch' && showFilters && (
                      <FiltersSwitch
                        label={f.label}
                        queryParam={f.queryParam}
                        defaultValue={f.defaultValue}
                        clearable={f.clearable}
                        className={cx(classes.filterSelect, f.className)}
                        // style={{ marginTop: 15, ...(f.style || {}) }}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>
          </Fragment>
        )}
      </div>
    );
  },
);
